import React, { useState, useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import { useTranslation } from 'react-i18next'
import { Heart, Target, Users, BookOpen, Award, User } from 'lucide-react'
import ResponsiveCarousel from './ResponsiveCarousel'
import TeamMemberCard from './TeamMemberCard'
import OptimizedImage from './OptimizedImage'
import { getManagementMembers } from '../api/managementService'
import '../styles/team-member-card.css'

const About = () => {
  const { t } = useTranslation()
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1
  })

  const [managementTeam, setManagementTeam] = useState([])
  const [loadingManagement, setLoadingManagement] = useState(true)

  const values = [
    {
      icon: <Heart className="w-8 h-8" />,
      title: t('about.characterBuilding'),
      description: t('about.characterBuildingDesc')
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: t('about.excellence'),
      description: t('about.excellenceDesc')
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: t('about.community'),
      description: t('about.communityDesc')
    },
    {
      icon: <BookOpen className="w-8 h-8" />,
      title: t('about.lifelongLearning'),
      description: t('about.lifelongLearningDesc')
    }
  ]

  // Fetch management team from API
  useEffect(() => {
    const fetchManagementTeam = async () => {
      try {
        setLoadingManagement(true)
        // Fetch all featured/active members for About page (all categories)
        const response = await getManagementMembers({ 
          isActive: true,
          // You can optionally filter by featured: true to show only featured members
          // featured: true 
        })
        
        if (response.status === 'success' && response.data?.members) {
          setManagementTeam(response.data.members)
        }
      } catch (error) {
        console.error('Error fetching management team:', error)
        setManagementTeam([])
      } finally {
        setLoadingManagement(false)
      }
    }

    fetchManagementTeam()
  }, [])

  return (
    <section id="about" className="py-16 lg:py-24 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div 
          ref={ref}
          className={`transition-all duration-1000 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold text-primary-purple font-poppins mb-4">
              {t('about.title')}
            </h2>
            <div className="w-24 h-1 bg-primary-yellow mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('about.subtitle')}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
            {/* Left Content */}
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl lg:text-3xl font-bold text-primary-blue mb-4 font-montserrat">
                  {t('about.mission')}
                </h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  {t('about.missionText')}
                </p>
                <p className="text-gray-700 leading-relaxed">
                  {t('about.missionInspiration')}
                </p>
              </div>

              <div>
                <h3 className="text-2xl lg:text-3xl font-bold text-primary-green mb-4 font-montserrat">
                  {t('about.vision')}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {t('about.visionText')}
                </p>
              </div>
            </div>

            {/* Right Content - Sardar Patel Heritage */}
            <div className="text-center">
              <div className="bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-shadow duration-300">
                <div className="w-56 h-56 mx-auto mb-6 rounded-full bg-gradient-to-br from-primary-brown to-primary-brown/80 p-1">
                  <div className="w-full h-full rounded-full bg-white flex items-center justify-center overflow-hidden">
                    {/* Sardar Patel image */}
                    <OptimizedImage
                      src="/about/sardar-patel.jpg" 
                      alt="Sardar Vallabhbhai Patel"
                      className="w-52 h-52 rounded-full object-cover"
                      loading="lazy"
                      width={224}
                      height={224}
                    />
                    {/* Fallback if image not available */}
                    <div className="w-52 h-52 rounded-full bg-gray-200 hidden items-center justify-center text-gray-500">
                      <Users size={80} />
                    </div>
                  </div>
                </div>
                <h4 className="text-3xl font-bold text-primary-brown mb-3">
                  {t('about.sardarPatelName')}
                </h4>
                <p className="text-gray-600 italic mb-4">
                  "{t('about.sardarPatelQuote')}"
                </p>
                <div className="text-sm text-gray-500">
                  <p className="font-semibold">{t('about.ironManOfIndia')}</p>
                  <p>{t('about.inspirationBehindSPET')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Trust History */}
          <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12 mb-16">
            <h3 className="text-2xl lg:text-3xl font-bold text-primary-purple mb-6 text-center font-montserrat">
              {t('about.ourLegacy')}
            </h3>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-4xl font-bold text-primary-yellow mb-2">{t('about.timeline.foundationYear')}</div>
                <h4 className="font-semibold text-primary-purple mb-2">{t('about.foundation')}</h4>
                <p className="text-gray-600 text-sm">
                  {t('about.foundationText')}
                </p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-primary-green mb-2">{t('about.timeline.expansionYear')}</div>
                <h4 className="font-semibold text-primary-purple mb-2">{t('about.expansion')}</h4>
                <p className="text-gray-600 text-sm">
                  {t('about.expansionText')}
                </p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-primary-blue mb-2">{t('about.timeline.excellenceYear')}</div>
                <h4 className="font-semibold text-primary-purple mb-2">{t('about.excellenceYear')}</h4>
                <p className="text-gray-600 text-sm">
                  {t('about.excellenceYearText')}
                </p>
              </div>
            </div>
          </div>

          {/* Management Team Section */}
          <div className="mb-16">
            {/* Section Title - Centered with balanced spacing */}
            <div className="text-center mb-8">
              <h3 className="text-2xl lg:text-3xl font-bold text-primary-purple font-montserrat">
                {t('about.ourTeam')}
              </h3>
              <div className="w-24 h-1 bg-primary-yellow mx-auto mt-4"></div>
            </div>
            
            {loadingManagement ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">{t('common.loading')}</p>
              </div>
            ) : managementTeam.filter(m => (m.categoryKey || m.category) === 'Trust').length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">{t('about.noManagementMembers')}</p>
              </div>
            ) : (
              <>
                {/* Responsive Carousel for Management Team */}
                <div className="management-carousel-wrapper">
                  <ResponsiveCarousel
                    autoplay={true}
                    autoplayDelay={5000}
                    pauseOnHover={true}
                    infinite={true}
                    showNavigation={true}
                    showDots={true}
                    className=""
                    desktopSlides={3}
                    tabletSlides={2}
                    mobileSlides={1}
                    spaceBetween={24}
                  >
                    {managementTeam.filter(m => (m.categoryKey || m.category) === 'Trust').map((member) => (
                      <TeamMemberCard key={member._id} member={member} />
                    ))}
                  </ResponsiveCarousel>
                </div>

                {/* Additional Management Info */}
                <div className="mt-10 bg-gradient-to-r from-primary-purple/5 to-primary-blue/5 rounded-xl p-6 lg:p-8 text-center">
                  <h4 className="text-lg font-bold text-primary-purple mb-4">
                    {t('about.leadershipExcellence')}
                  </h4>
                  <p className="text-sm lg:text-base text-gray-700 max-w-3xl mx-auto leading-relaxed">
                    {t('about.leadershipExcellenceText')}
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Teaching Staff Section */}
          <div className="mb-16">
            {/* Section Title - Centered with balanced spacing */}
            <div className="text-center mb-8">
              <h3 className="text-2xl lg:text-3xl font-bold text-primary-purple font-montserrat">
                {t('management.faculty')}
              </h3>
              <div className="w-24 h-1 bg-primary-yellow mx-auto mt-4"></div>
            </div>
            
            {loadingManagement ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">{t('common.loading')}</p>
              </div>
            ) : managementTeam.filter(m => (m.categoryKey || m.category) === 'Teaching Staff').length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">{t('about.noTeachingStaff')}</p>
              </div>
            ) : (
              <>
                {/* Responsive Carousel for Teaching Staff */}
                <div className="management-carousel-wrapper">
                  <ResponsiveCarousel
                    autoplay={true}
                    autoplayDelay={5000}
                    pauseOnHover={true}
                    infinite={true}
                    showNavigation={true}
                    showDots={true}
                    className=""
                    desktopSlides={3}
                    tabletSlides={2}
                    mobileSlides={1}
                    spaceBetween={24}
                  >
                    {managementTeam.filter(m => (m.categoryKey || m.category) === 'Teaching Staff').map((member) => (
                      <TeamMemberCard key={member._id} member={member} />
                    ))}
                  </ResponsiveCarousel>
                </div>

                {/* Additional Teaching Staff Info */}
                <div className="mt-10 bg-gradient-to-r from-primary-purple/5 to-primary-blue/5 rounded-xl p-6 lg:p-8 text-center">
                  <h4 className="text-lg font-bold text-primary-purple mb-4">
                    {t('about.dedicatedEducators')}
                  </h4>
                  <p className="text-sm lg:text-base text-gray-700 max-w-3xl mx-auto leading-relaxed">
                    {t('about.dedicatedEducatorsText')}
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Core Values */}
          <div>
            <h3 className="text-2xl lg:text-3xl font-bold text-center text-primary-purple mb-12 font-montserrat">
              {t('about.values')}
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <div 
                  key={index}
                  className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                >
                  <div className="text-primary-purple mb-4 flex justify-center">
                    {value.icon}
                  </div>
                  <h4 className="text-lg font-semibold text-primary-purple mb-3">
                    {value.title}
                  </h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default About